import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { TrendTrack } from "../../../../types"
import { Bar, Line, Pie } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from "chart.js"
import { lang } from "../../../../constants/languages"
import { KSpan } from "kahuna-base-react-components"
import { colorPaletteOptions, platformColorPalette } from "../../../../constants"
import { withAlpha, convertToDashedDate, capitalize, getDailyTrendsChartTitle, getDailyTrendsEventText } from "../../../../utility"
import { ChartIndex, EnlargeIcon } from "../Trends"
import NotFound from "../../../NotFound"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement)

export type HoveredIndex = {
  datasetIndex: number
  dataIndex: number
}

const BarChartComponent = ({ data, _key, hideDatalabels, eventText }: { data: any[]; _key: string; hideDatalabels?: boolean, eventText?:string }) => {
  const totals = useMemo(() => {
    const trackTotals = {}
    const trackStreamMap = {}

    data.forEach((item) => {
      const value = _key === "trackTitle" ? capitalize(item.track?.title) : capitalize(item[_key])
      const keyValue = value ?? "-"
      if (!trackStreamMap[keyValue]) trackStreamMap[keyValue] = 0
      trackStreamMap[keyValue] += item.streams
    })

    const top10List = Object.entries(trackStreamMap)
      //@ts-ignore
      .sort((a, b) => b[1] - a[1]) // sort descending by value
      .slice(0, 10) // take top 10
      .map(([key]) => key)

    data.forEach((item) => {
      const value = _key === "trackTitle" ? capitalize(item.track?.title) : capitalize(item[_key])
      const rawValue = value ?? "-"
      const keyValue = top10List.includes(rawValue) ? rawValue : lang.trends.others
      if (!trackTotals[keyValue]) trackTotals[keyValue] = 0
      trackTotals[keyValue] += item.streams
    })

    // ts-ignore
    const sortedTotals = Object.fromEntries(
      (Object.entries(trackTotals) as [string, number][]).sort((a, b) => b[1] - a[1])
    )

    const labels = Object.keys(sortedTotals)
    const values = labels.map((keyValue) => sortedTotals[keyValue])

    return {
      labels,
      datasets: [
        {
          label: `Total ${lang.trends.streams} by Track`,
          data: values,
          backgroundColor: labels.map((label, index) =>
            _key === "platform" ? platformColorPalette[label] : colorPaletteOptions[index]
          ),
          borderColor: "#FFFFFF",
          borderWidth: 1,
          borderSkipped: false
        }
      ]
    }
  }, [data])

  const [animationCompleted, setAnimationCompleted] = useState(false)

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        onComplete: () => {
          setAnimationCompleted(true)
        },
        delay: (ctx) => {
          //Hide datalabels in animation process
          if (ctx.type === "data" && ctx.mode === "default") {
            return 0
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          displayColors: false,
          caretSize: 0,
          caretPadding: 10,
          xAlign: "center" as const,
          yAlign: "bottom" as const,
          padding: 10,
          backgroundColor: "#333",
          titleColor: "#FFF",
          bodyColor: "#FFF",

          bodyFont: {
            family: "Inter",
            weight: 400,
            color: "#000",
            size: 12,
            lineHeight: "16px"
          },
          titleFont: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "16px"
          },

          callbacks: {
            label: (context) => {
              const value = context.raw
              return `${context.label}: ${value.toLocaleString()} ${eventText || "streams"}`
            }
          }
        },
        datalabels: {
          display: (ctx) => {
            if (hideDatalabels) return false
            if (!animationCompleted) return false

            const chart = ctx.chart
            const meta = chart.getDatasetMeta(ctx.datasetIndex)

            if (!meta?.data?.[ctx.dataIndex]) return false

            const bar = meta.data[ctx.dataIndex]
            const barHeight = Math.abs(bar.base - bar.y)
            const minHeight = 15

            return barHeight >= minHeight
          },
          color: (ctx) => {
            const bg = Array.isArray(ctx.dataset.backgroundColor)
              ? ctx.dataset.backgroundColor[ctx.dataIndex]
              : ctx.dataset.backgroundColor

            return bg === "#DAF7E0" ? "#000" : "#FFF"
          },
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "12px"
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 12,
              lineHeight: "12px"
            },
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45,
            callback: (value, _index, _ticks) => {
              const xAxisLabel = totals.labels[_index]
              if (_key === "platform" || xAxisLabel.length <= 12) return xAxisLabel
              else {
                if (xAxisLabel.length > 12) {
                  return `${xAxisLabel.slice(0, 9)}...`
                }
              }
            }
          }
        },
        y: {
          grid: {
            display: true,
            color: (ctx, index) => {
              return ctx.tick.value === ctx.scale.min ? "transparent" : "rgba(183, 183, 183, 0.30)"
            },
            drawTicks: true
          },
          border: {
            display: true,
            dash: [3, 3]
          },
          ticks: {
            color: "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 11,
              lineHeight: "12px"
            }
          }
        }
      },
      elements: {
        bar: {
          borderRadius: {
            topRight: 10,
            topLeft: 10,
            bottomRight: 10,
            bottomLeft: 10
          }
        }
      }
    }),
    [totals, animationCompleted]
  )

  return (
    <Bar
      data={totals}
      options={options}
      style={{
        width: "100%",
        height: "100%"
      }}
    />
  )
}

export const PlatformsLineChartComponent = ({
  data,
  hoveredIndex,
  setHoveredIndex,
  eventText
}: {
  data: any[]
  hoveredIndex: HoveredIndex
  setHoveredIndex: React.Dispatch<React.SetStateAction<HoveredIndex>>
  eventText?:string
}) => {
  const changeColorOnHover = (index: number, color: string) => {
    const otherLineHovered = !(hoveredIndex.datasetIndex === index || hoveredIndex.datasetIndex === -1)
    return otherLineHovered ? withAlpha(color, 0.4) : color
  }

  const handleHover = useCallback(
    (_evt: unknown, elements: any[]) => {
      setHoveredIndex((prev: HoveredIndex) => {
        if (elements.length) {
          const { datasetIndex, index: dataIndex } = elements[0]

          if (datasetIndex === prev.datasetIndex && dataIndex === prev.dataIndex) {
            return prev
          }

          return { datasetIndex, dataIndex }
        }
        if (prev.datasetIndex === -1 && prev.dataIndex === -1) {
          return prev
        }
        return { datasetIndex: -1, dataIndex: -1 }
      })
    },

    [hoveredIndex, setHoveredIndex]
  )

  const baseChart = useMemo(() => {
    const platformStreamMap = {}
    const allPlatforms: string[] = []

    data.forEach((item: TrendTrack) => {
      const date = convertToDashedDate(item.date)
      const platform = capitalize(item?.platform ?? "-")

      // Streams per platform
      if (!platformStreamMap[date]) platformStreamMap[date] = {}
      if (!platformStreamMap[date][platform]) platformStreamMap[date][platform] = { streams: 0 }
      platformStreamMap[date][platform]["streams"] += item.streams

      if (!allPlatforms.includes(platform)) allPlatforms.push(platform)
    })

    const labels = Object.keys(platformStreamMap).sort()

    const datasets: any = []
    allPlatforms.forEach((platform, index) => {
      const data: number[] = []
      labels.forEach((label) => {
        // label = date
        if (platformStreamMap[label][platform]?.streams) {
          data.push(platformStreamMap[label][platform].streams)
        } else {
          data.push(0)
        }
      })
      const randomColor = platformColorPalette[platform] || colorPaletteOptions[index]
      const isSinglePoint = labels?.length === 1
      datasets.push({
        label: platform,
        data,
        borderColor: randomColor,
        backgroundColor: randomColor,
        pointBorderColor: "transparent",
        tension: 0.4,
        pointRadius: isSinglePoint ? 5 : 0
      })
    })

    return {
      labels,
      datasets
    }
  }, [data])

  const chartData = useMemo(
    () => ({
      labels: baseChart.labels,
      datasets: baseChart.datasets.map((ds, i) => {
        const color = changeColorOnHover(i, ds.backgroundColor)
        return {
          ...ds,
          borderColor: color,
          backgroundColor: color
        }
      })
    }),
    [baseChart, hoveredIndex]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      clip: false as const,
      interaction: {
        intersect: false,
        mode: "nearest" as const
      },

      plugins: {
        datalabels: {
          display: false
        },
        tooltip: {
          displayColors: false,
          backgroundColor: "#333",
          xAlign: "center" as const,
          yAlign: "bottom" as const,
          caretSize: 0,
          caretPadding: 10,
          padding: 10,
          bodyFont: (context) => {
            const chart = context.chart
            const tooltip = context.tooltip
            const canvasHeight = chart.height
            const maxTooltipHeight = canvasHeight * 0.85

            let realLineCount = 0
            if (tooltip.body) {
              tooltip.body.forEach((item) => {
                realLineCount += item.lines.length
              })
            } else {
              realLineCount = 1
            }

            if (tooltip.dataPoints && tooltip.dataPoints.length > 0) {
              const afterBodyCb = tooltip.options?.callbacks?.afterBody
              if (typeof afterBodyCb === "function") {
                const afterBodyLines = afterBodyCb(tooltip.dataPoints)
                if (Array.isArray(afterBodyLines)) {
                  realLineCount += afterBodyLines.length
                }
              }
            }

            let fontSize = 12
            let lineHeight = fontSize + 4
            let estimatedHeight = realLineCount * lineHeight + 30

            while (estimatedHeight > maxTooltipHeight && fontSize > 8) {
              fontSize -= 1
              lineHeight = fontSize + 4
              estimatedHeight = realLineCount * lineHeight + 30
            }

            return {
              family: "Inter",
              weight: 400,
              size: fontSize,
              lineHeight: `${lineHeight}px`
            }
          },

          titleFont: {
            family: "Inter",
            weight: 600,
            size: 14,
            lineHeight: "16px"
          },
          intersect: false,
          callbacks: {
            afterBody: (context) => {
              const _type = context[0].dataset._type
              if (_type !== "streams") {
                return ""
              }
              const date = context[0].label
              //@ts-ignore
              const sorted = Object.entries(tracks).sort((a, b) => b[1] - a[1])
              return ""
            }
          }
        },
        hover: {
          intersect: false
        },
        legend: {
          display: true,
          position: "bottom" as const,
          align: "end" as const,
          labels: {
            font: {
              family: "Inter",
              weight: 500,
              size: 13,
              lineHeight: "13px"
            },
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8
          }
        }
      },
      scales: {
        y: {
          grid: {
            display: true,
            color: (ctx, index) => {
              return ctx.tick.value === ctx.scale.min ? "transparent" : "rgba(183, 183, 183, 0.30)"
            },
            drawTicks: true
          },

          border: {
            display: true,
            dash: [3, 3]
          },
          ticks: {
            color: hoveredIndex.datasetIndex === 0 ? "#111" : "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 11,
              lineHeight: "12px"
            }
          }
        },
        x: {
          grid: {
            display: true,
            drawBorder: true,
            drawTicks: true,
            drawOnChartArea: false
          },
          border: {
            display: true
          },
          ticks: {
            color: "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 11,
              lineHeight: "12px"
            }
          }
        }
      },
      onHover: handleHover
    }),
    [handleHover, chartData]
  )

  return <Line data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
}

const LineChartComponent = ({
  data,
  hoveredIndex,
  setHoveredIndex,
  eventText
}: {
  data: any[]
  hoveredIndex: HoveredIndex
  setHoveredIndex: React.Dispatch<React.SetStateAction<HoveredIndex>>
  eventText?:string
}) => {
  const changeColorOnHover = (index: number, color: string) => {
    const otherLineHovered = !(hoveredIndex.datasetIndex === index || hoveredIndex.datasetIndex === -1)
    return otherLineHovered ? withAlpha(color, 0.4) : color
  }

  const handleHover = useCallback(
    (_evt: unknown, elements: any[]) => {
      setHoveredIndex((prev: HoveredIndex) => {
        if (elements.length) {
          const { datasetIndex, index: dataIndex } = elements[0]

          if (datasetIndex === prev.datasetIndex && dataIndex === prev.dataIndex) {
            return prev
          }

          return { datasetIndex, dataIndex }
        }
        if (prev.datasetIndex === -1 && prev.dataIndex === -1) {
          return prev
        }
        return { datasetIndex: -1, dataIndex: -1 }
      })
    },

    [hoveredIndex, setHoveredIndex]
  )

  const baseChart = useMemo(() => {
    const grouped = {}
    const trackMap = {}

    data.forEach((item) => {
      const date = convertToDashedDate(item.date)
      const title = item.track?.title ?? "-"

      // Aggregate totals for main datasets
      if (!grouped[date]) grouped[date] = { streams: 0, skips: 0, saves: 0 }
      grouped[date].streams += item.streams
      grouped[date].skips += item.skips
      grouped[date].saves += item.saves

      // Track streams per title
      if (!trackMap[date]) trackMap[date] = {}
      if (!trackMap[date][title]) trackMap[date][title] = 0
      trackMap[date][title] += item.streams
    })

    const labels = Object.keys(grouped).sort()
    const isSinglePoint = labels?.length === 1

    return {
      labels,
      datasets: [
        {
          label: `${eventText || lang.trends.streams}`,
          data: labels.map((d) => grouped[d].streams),
          borderColor: changeColorOnHover(0, colorPaletteOptions[0]),
          backgroundColor: changeColorOnHover(0, colorPaletteOptions[0]),
          tension: 0.4,
          pointRadius: isSinglePoint ? 5 : 0,
          _type: "streams",
          yAxisID: "y"
        },
        {
          label: lang.trends.skips,
          data: labels.map((d) => grouped[d].skips),
          borderColor: changeColorOnHover(1, colorPaletteOptions[1]),
          backgroundColor: changeColorOnHover(1, colorPaletteOptions[1]),
          tension: 0.4,
          pointRadius: isSinglePoint ? 5 : 0,
          _type: "skips"
        },
        {
          label: lang.trends.saves,
          data: labels.map((d) => grouped[d].saves),
          borderColor: changeColorOnHover(2, colorPaletteOptions[2]),
          backgroundColor: changeColorOnHover(2, colorPaletteOptions[2]),
          tension: 0.4,
          pointRadius: isSinglePoint ? 5 : 0,
          _type: "saves"
        }
      ],
      trackMap
    }
  }, [data])

  const chartData = useMemo(
    () => ({
      labels: baseChart.labels,
      datasets: baseChart.datasets.map((ds, i) => {
        const color = changeColorOnHover(i, ds.backgroundColor)
        return {
          ...ds,
          borderColor: color,
          backgroundColor: color
        }
      }),
      trackMap: baseChart.trackMap
    }),
    [baseChart, hoveredIndex]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      clip: false as const,
      interaction: {
        intersect: false,
        mode: "nearest" as const
      },

      plugins: {
        datalabels: {
          display: false
        },
        tooltip: {
          displayColors: false,
          backgroundColor: "#333",
          xAlign: "center" as const,
          yAlign: "bottom" as const,
          caretSize: 0,
          caretPadding: 10,
          padding: 10,
          bodyFont: (context) => {
            const chart = context.chart
            const tooltip = context.tooltip
            const canvasHeight = chart.height
            const maxTooltipHeight = canvasHeight * 0.85

            let realLineCount = 0
            if (tooltip.body) {
              tooltip.body.forEach((item) => {
                realLineCount += item.lines.length
              })
            } else {
              realLineCount = 1
            }

            if (tooltip.dataPoints && tooltip.dataPoints.length > 0) {
              const afterBodyCb = tooltip.options?.callbacks?.afterBody
              if (typeof afterBodyCb === "function") {
                const afterBodyLines = afterBodyCb(tooltip.dataPoints)
                if (Array.isArray(afterBodyLines)) {
                  realLineCount += afterBodyLines.length
                }
              }
            }

            let fontSize = 12
            let lineHeight = fontSize + 4
            let estimatedHeight = realLineCount * lineHeight + 30

            while (estimatedHeight > maxTooltipHeight && fontSize > 8) {
              fontSize -= 1
              lineHeight = fontSize + 4
              estimatedHeight = realLineCount * lineHeight + 30
            }

            return {
              family: "Inter",
              weight: 400,
              size: fontSize,
              lineHeight: `${lineHeight}px`
            }
          },

          titleFont: {
            family: "Inter",
            weight: 600,
            size: 14,
            lineHeight: "16px"
          },
          intersect: false,
          callbacks: {
            afterBody: (context) => {
              const _type = context[0].dataset._type
              if (_type !== "streams") {
                return ""
              }
              const date = context[0].label
              const tracks = chartData.trackMap[date] || {}
              //@ts-ignore
              const sorted = Object.entries(tracks).sort((a, b) => b[1] - a[1])
              return ["", ...sorted.map(([title]) => `• ${title}: ${tracks[title]}`)]
            }
          }
        },
        hover: {
          intersect: false
        },
        legend: {
          display: true,
          position: "bottom" as const,
          align: "end" as const,
          labels: {
            font: {
              family: "Inter",
              weight: 500,
              size: 13,
              lineHeight: "13px"
            },
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            generateLabels: (chart) => {
              const datasets = chart.data.datasets
              const dateIndex = chart.tooltip?.dataPoints?.[0]?.dataIndex ?? null
              const trackMap = chartData.trackMap

              return datasets.map((dataset, i) => {
                let streamLabel = dataset.label
                if (dataset.label === "Streams" && dateIndex !== null) {
                  const date = chart.data.labels[dateIndex]
                  const perTrack = trackMap?.[date] ?? {}
                  const detail = Object.entries(perTrack)
                    //@ts-ignore
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3) // limit for readability
                    .map(([title, count]) => `${title}: ${count?.toLocaleString()}`)
                    .join(", ")
                  streamLabel = `${lang.trends.streams} (${detail})`
                }

                return {
                  text: streamLabel,
                  fillStyle: dataset.backgroundColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: 0,
                  fontColor: "#000",
                  hidden: !chart.isDatasetVisible(i),
                  index: i
                }
              })
            }
          }
        }
      },
      scales: {
        y: {
          grid: {
            display: true,
            color: (ctx, index) => {
              return ctx.tick.value === ctx.scale.min ? "transparent" : "rgba(183, 183, 183, 0.30)"
            },
            drawTicks: true
          },

          border: {
            display: true,
            dash: [3, 3]
          },
          ticks: {
            color: hoveredIndex.datasetIndex === 0 ? "#111" : "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 11,
              lineHeight: "12px"
            }
          }
        },
        x: {
          grid: {
            display: true,
            drawBorder: true,
            drawTicks: true,
            drawOnChartArea: false
          },
          border: {
            display: true
          },
          ticks: {
            color: "#999",
            font: {
              family: "Inter",
              weight: 500,
              size: 11,
              lineHeight: "12px"
            }
          }
        }
      },
      onHover: handleHover
    }),
    [handleHover, chartData]
  )

  return <Line data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
}

const PieChartComponent = ({ data, hideDatalabels, eventText }: { data: any[]; hideDatalabels?: boolean, eventText?:string }) => {
  const [totalStream, setTotalStream] = useState<number>(0)
  const pieChartRef = useRef<ChartJS | null>(null)
  const totals = useMemo(() => {
    const platformTotals = {}

    data.forEach((item: TrendTrack) => {
      const platform = capitalize(item?.platform ?? "-")
      if (!platformTotals[platform]) platformTotals[platform] = 0
      platformTotals[platform] += item.streams
    })

    // ts-ignore
    const sortedTotals = Object.fromEntries(
      (Object.entries(platformTotals) as [string, number][]).sort((a, b) => b[1] - a[1])
    )

    const labels = Object.keys(sortedTotals)
    const values = labels.map((label) => sortedTotals[label])

    const total = values.reduce((sum: number, value) => sum + Number(value), 0)
    setTotalStream(total)

    return {
      labels,
      datasets: [
        {
          label: `Streams by Platforms`,
          data: values,
          backgroundColor: labels.map((label, index) => platformColorPalette[label]),
          borderColor: "#fff",
          borderWidth: 1
        }
      ]
    }
  }, [data])

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          },
          color: "#000",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8
        },
        onHover: (event, legendItem, legend) => {
          const chart = pieChartRef.current
          if (chart && chart.tooltip && chart.data && chart.data.datasets && chart.data.datasets[0]) {
            // Set the active element to the corresponding slice
            try {
              chart.setActiveElements([{ datasetIndex: 0, index: legendItem.index }])
              chart.tooltip?.setActiveElements([{ datasetIndex: 0, index: legendItem.index }], { x: 0, y: 0 }) // Optionally, set tooltip position
              chart.update()
            } catch (error) {
              // do nothing
            }
          }
        },
        onLeave: (event, legendItem, legend) => {
          const chart = pieChartRef.current
          if (chart && chart?.tooltip) {
            try {
              chart.setActiveElements([])
              chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
              chart.update()
            } catch (error) {
              // do nothing
            }
          }
        }
      },
      tooltip: {
        displayColors: false,
        padding: 10,
        backgroundColor: "#FFF",
        titleColor: "#000",
        bodyColor: "#000",
        borderColor: "#E5E7EB",
        borderWidth: 1,
        bodyFont: {
          family: "Inter",
          weight: 400,
          color: "#000",
          size: 12,
          lineHeight: "16px"
        },
        titleFont: {
          family: "Inter",
          weight: 500,
          size: 13,
          lineHeight: "16px"
        },
        callbacks: {
          label: (context) => {
            const data = context.dataset.data as number[]
            const value = context.raw

            let percentage = 0

            if (totalStream <= 0) {
              percentage = 0
            } else {
              percentage = Number(((value / totalStream) * 100).toFixed(1))

              const isLast = context.dataIndex === data.length - 1

              if (isLast) {
                const prevSum = data.slice(0, -1).reduce((s, v) => s + Number(((v / totalStream) * 100).toFixed(1)), 0)
                percentage = Number((100 - prevSum).toFixed(1))
              }
            }

            return `${context.label}: ${value.toLocaleString()} ${eventText || lang.trends.streams} (%${percentage})`
          }
        }
      },
      datalabels: {
        display: (context) => {
          if (hideDatalabels) return false
          const active = context.chart.getActiveElements()
          if (active && active.length > 0 && context.dataIndex === active[0].index) {
            return true
          }
          return "auto"
        },
        color: "#fff",
        formatter: (value, context) => {
          const label = context.chart.data.labels[context.dataIndex]
          return label // or `${label}: ${value}` for more info
        },
        font: {
          family: "Inter",
          weight: 500,
          size: 14,
          lineHeight: "16px"
        }
      }
    }
  }

  return (
    <Pie
      //@ts-ignore
      ref={pieChartRef}
      data={totals}
      //@ts-ignore
      options={options}
      style={{
        width: "100%",
        height: "100%"
      }}
    />
  )
}

type StreamsDailyProps = {
  streamsDaily: TrendTrack[]
  fullScreenChartIndex: number | undefined
  setFullScreenChartIndex: (index: number | undefined) => void
  eventType: "dsp" | "social_consumption" | "social_production" | "download"
}

const StreamsDaily: FC<StreamsDailyProps> = (props) => {
  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })
  const [platformHoveredIndex, setPlatformHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })
  const { streamsDaily, fullScreenChartIndex, setFullScreenChartIndex } = props
  const [filteredStreamsDaily, setFilteredStreamsDaily] = useState<TrendTrack[]>([])
  useEffect(() => {
    if (props.eventType === "social_consumption") {
      const filteredStreams = streamsDaily.filter((stream) => stream.eventType === "social_consumption")
      setFilteredStreamsDaily(filteredStreams)
    } else if (props.eventType === "social_production") {
      const filteredStreams = streamsDaily.filter((stream) => stream.eventType === "social_production")
      setFilteredStreamsDaily(filteredStreams)
    } else {
      setFilteredStreamsDaily(streamsDaily)
    }
  }, [props.eventType, streamsDaily])

  return (
    <div className="w-full flex-1 min-h-0 relative">
      {streamsDaily && !(streamsDaily.length > 0) && (
        <div
          className="bg-[#FFF] absolute left-0 top-0 w-full h-full flex items-center justify-center rounded-[10px]"
          style={{
            borderRadius: "10px",
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: "var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
        >
          <div className="flex justify-center">
            <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
          </div>
        </div>
      )}
      {fullScreenChartIndex && (
        <div
          className="bg-[#FFF] absolute left-0 top-0 w-full h-full flex items-center justify-center rounded-[10px]"
          style={{
            borderRadius: "10px",
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: "var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
        >
          <div className="w-full h-full py-5 px-7 flex justify-center items-center relative flex-col gap-4">
            <div
              onClick={() => {
                setFullScreenChartIndex(undefined)
              }}
              className="flex items-center cursor-pointer justify-center p-1 rounded-[4px] hover:bg-[#F7F7F7] absolute right-2 top-2"
            >
              <img src={"/close.svg"} className="w-4 h-4" />
            </div>
            <KSpan
              text={getDailyTrendsChartTitle(fullScreenChartIndex, props.eventType)}
              fontSize={16}
              color="#000"
              fontWeight={500}
            />
            <div
              className="w-full flex-1 min-h-0 flex items-center justify-center"
              onMouseLeave={() => {
                if (fullScreenChartIndex === ChartIndex.OVERVIEW_LINE_CHART_PLATFORM) {
                  setPlatformHoveredIndex((prev) => {
                    return { ...prev, datasetIndex: -1, dataIndex: -1 }
                  })
                } else if (fullScreenChartIndex === ChartIndex.OVERVIEW_LINE_CHART_TRACK) {
                  setHoveredIndex((prev) => {
                    return { ...prev, datasetIndex: -1, dataIndex: -1 }
                  })
                }
              }}
            >
              {fullScreenChartIndex === ChartIndex.OVERVIEW_BAR_CHART_TRACK && (
                <BarChartComponent data={filteredStreamsDaily} _key={"trackTitle"} eventText={getDailyTrendsEventText(props.eventType)}/>
              )}
              {fullScreenChartIndex === ChartIndex.OVERVIEW_PIE_CHART_PLATFORM && (
                <PieChartComponent data={filteredStreamsDaily} eventText={getDailyTrendsEventText(props.eventType)} />
              )}
              {fullScreenChartIndex === ChartIndex.OVERVIEW_BAR_CHART_PLATFORM && (
                <BarChartComponent data={filteredStreamsDaily} _key={"platform"} eventText={getDailyTrendsEventText(props.eventType)} />
              )}
              {fullScreenChartIndex === ChartIndex.OVERVIEW_LINE_CHART_TRACK && (
                <LineChartComponent
                  data={filteredStreamsDaily}
                  setHoveredIndex={setHoveredIndex}
                  hoveredIndex={hoveredIndex}
                  eventText={getDailyTrendsEventText(props.eventType)}
                />
              )}
              {fullScreenChartIndex === ChartIndex.OVERVIEW_LINE_CHART_PLATFORM && (
                <PlatformsLineChartComponent
                  data={filteredStreamsDaily}
                  hoveredIndex={platformHoveredIndex}
                  setHoveredIndex={setPlatformHoveredIndex}
                  eventText={getDailyTrendsEventText(props.eventType)}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className="w-full h-full flex flex-col gap-3"
        style={{
          opacity: fullScreenChartIndex || (streamsDaily && !(streamsDaily.length > 0)) ? 0 : 1,
          pointerEvents: fullScreenChartIndex || (streamsDaily && !(streamsDaily.length > 0)) ? "none" : "auto"
        }}
      >
        <div className="flex flex-row gap-3 w-full flex-1 min-h-0">
          <div
            className="py-5 px-7 flex-1 min-h-0 flex flex-col gap-2 h-full relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.OVERVIEW_BAR_CHART_TRACK} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.OVERVIEW_BAR_CHART_TRACK, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-grow w-full min-h-0">
              <BarChartComponent data={filteredStreamsDaily} _key={"trackTitle"} hideDatalabels={true} eventText={getDailyTrendsEventText(props.eventType)}/>
            </div>
          </div>
          <div
            className="py-5 px-7 h-full aspect-square flex flex-col gap-2 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.OVERVIEW_PIE_CHART_PLATFORM} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.OVERVIEW_PIE_CHART_PLATFORM, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-1 min-h-0 aspect-square flex items-center justify-center">
              <PieChartComponent data={filteredStreamsDaily} hideDatalabels={true} eventText={getDailyTrendsEventText(props.eventType)}/>
            </div>
          </div>
          <div
            className="py-5 px-7 flex-1 min-h-0 flex flex-col gap-2 h-full relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.OVERVIEW_BAR_CHART_PLATFORM}  />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.OVERVIEW_BAR_CHART_PLATFORM, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-grow w-full min-h-0">
              <BarChartComponent data={filteredStreamsDaily} _key={"platform"} hideDatalabels={true} eventText={getDailyTrendsEventText(props.eventType)} />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3 w-full flex-1 min-h-0">
          <div
            onMouseLeave={() => {
              setHoveredIndex((prev) => {
                return { ...prev, datasetIndex: -1, dataIndex: -1 }
              })
            }}
            className="py-5 px-7 flex-1 flex flex-col gap-2 min-h-0 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.OVERVIEW_LINE_CHART_TRACK} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.OVERVIEW_LINE_CHART_TRACK, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-grow min-h-0 w-full">
              <LineChartComponent
                data={filteredStreamsDaily}
                setHoveredIndex={setHoveredIndex}
                hoveredIndex={hoveredIndex}
                eventText={getDailyTrendsEventText(props.eventType)}
              />
            </div>
          </div>
          <div
            onMouseLeave={() => {
              setPlatformHoveredIndex((prev) => {
                return { ...prev, datasetIndex: -1, dataIndex: -1 }
              })
            }}
            className="py-5 px-7 flex-1 flex flex-col gap-2 min-h-0 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.OVERVIEW_LINE_CHART_PLATFORM} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.OVERVIEW_LINE_CHART_PLATFORM, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-grow min-h-0 w-full">
              <PlatformsLineChartComponent
                data={filteredStreamsDaily}
                hoveredIndex={platformHoveredIndex}
                setHoveredIndex={setPlatformHoveredIndex}
                eventText={getDailyTrendsEventText(props.eventType)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StreamsDaily
